import React, { useState } from 'react';
import { Flex, Text } from 'rebass';
import { Input, Button } from '@lomadee/design-system';
import { useCreateOrganizationContext } from '@plug/contexts/CreateOrganizationContext';
import { Formik } from 'formik';
import { OrganizationRegisterNewPasswordSchema } from './OrganizationRegistrationPassword.schema';
import { Spacer } from '@plug/ui';
import { TitleForm } from '../../../affiliate-registration/style';
import { Trans, useTranslation } from 'react-i18next';

const OrganizationRegistrationPasswordStep: React.FC = () => {
  const { t } = useTranslation();
  const { values: contextValues, setStep, addValues } = useCreateOrganizationContext();
  const [showPasswordInputs, setShowPasswordInputs] = useState({
    password: false,
    passwordConfirmation: false,
  });

  const handleSubmitFormik = (formikValues: CreateOrganizationFormI): void => {
    addValues(formikValues);
    setStep('PERSONAL_DATA');
  };

  const toggleShowPasswordInput = (passwordInput: keyof typeof showPasswordInputs): void => {
    setShowPasswordInputs({
      ...showPasswordInputs,
      [passwordInput]: !showPasswordInputs[passwordInput],
    });
  };

  return (
    <Flex flexDirection="column" sx={{ gap: '32px' }}>
      <Spacer mt="64px" />

      <TitleForm>
        <Trans i18nKey="hubly-dashboard.signup.admin.right-side.first-step.title" />
      </TitleForm>

      <Formik
        initialValues={contextValues}
        validationSchema={OrganizationRegisterNewPasswordSchema}
        validateOnMount={true}
        onSubmit={handleSubmitFormik}
      >
        {({
          values,
          handleChange,
          errors,
          isValid,
          handleSubmit,
          handleBlur,
          touched,
        }): JSX.Element => (
          <>
            <Input
              label={t('hubly-dashboard.signup.admin.right-side.first-step.form.password.label')}
              placeholder={t(
                'hubly-dashboard.signup.admin.right-side.first-step.form.password.placeholder',
              )}
              name="password"
              value={values.password}
              onBlur={handleBlur}
              onChange={handleChange}
              error={touched.password && !!errors.password}
              type={showPasswordInputs.password ? 'text' : 'password'}
              helperText={t(
                'hubly-dashboard.signup.admin.right-side.first-step.form.password.helper',
              )}
              iconRight={showPasswordInputs.password ? 'eye' : 'eyeSlash'}
              iconRightOnClick={(): void => toggleShowPasswordInput('password')}
              fluid
            />

            <Input
              label={t(
                'hubly-dashboard.signup.admin.right-side.first-step.form.confirm-password.label',
              )}
              placeholder={t(
                'hubly-dashboard.signup.admin.right-side.first-step.form.confirm-password.placeholder',
              )}
              name="passwordConfirmation"
              value={values.passwordConfirmation}
              onBlur={handleBlur}
              onChange={handleChange}
              error={touched.passwordConfirmation && !!errors.passwordConfirmation}
              helperText={touched.passwordConfirmation ? errors.passwordConfirmation : ''}
              type={showPasswordInputs.passwordConfirmation ? 'text' : 'password'}
              iconRight={showPasswordInputs.passwordConfirmation ? 'eye' : 'eyeSlash'}
              iconRightOnClick={(): void => toggleShowPasswordInput('passwordConfirmation')}
              fluid
            />

            <Button type="submit" disabled={!isValid} onClick={(): void => handleSubmit()}>
              {t('hubly-dashboard.signup.admin.right-side.common.form.buttons.confirm.text')}
            </Button>
          </>
        )}
      </Formik>
    </Flex>
  );
};

export default OrganizationRegistrationPasswordStep;
